import {
  Box,
  Button,
  Checkbox,
  createFilterOptions,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Typography,
} from "@mui/material";
import { get, kebabCase, set } from "lodash";
import { FC, FormEvent, useEffect, useState } from "react";
import { HistoryProps } from "../..";
import { medicalsApi } from "../../../../../../api/medicalsApi";
import ComponentLoader from "../../../../../../components/ComponentLoader";
import { NotificationVariant } from "../../../../../../components/dashboard/NotificationsProvider";
import AutocompleteFreeOptions from "../../../../../../components/form/AutocompleteFreeOptions";
import ConditionInputNew from "../../../../../../components/form/ConditionInputNew";
import NotesInput from "../../../../../../components/form/NotesInput";
import Quantity from "../../../../../../components/widgets/inputs/Quantity";
import CodingSystems from "../../../../../../constants/CodingSystems";
import useMedicalEncounter from "../../../../../../hooks/useMedicalEncounter";
import useNotifications from "../../../../../../hooks/useNotifications";
import usePractitioner from "../../../../../../hooks/usePractitioner";
import { familyHistoryRelationships } from "../../../../../../slices/options/family";
import { CodeableConcept } from "../../../../../../types/codeableConcept";
import { Quantity as QuantityType } from "../../../../../../types/quantity";
import { createEncounterParam } from "../../../../../../utils/DataTransformationUtils";
import { noteManager, NoteWithUuid } from "../../../../../../utils/noteManager";
import DialogPrompt from "../../../components/Dialog";
import {
  FamilyHistory,
  FamilyHistoryConditionFormatted,
} from "../../../types/familyHistoryTypes";
import {
  applyRemovedFamilyHistoryRelationship,
  buildSaveableFamilyHistoryItems,
  conditionCodeOf,
  getFamilyHistoryItemsToPersist,
  overlayRelationshipDeceased,
  getFamilyHistoryItemsToDeleteCondition,
  relationshipCode,
  sameConditionCode,
} from "../../../utils/formatFamilyHistory";

type RelationshipOption = (typeof familyHistoryRelationships)[0] & {
  version?: "creatable";
};

type RelationshipStatus = {
  [key: string]: {
    isDeceased: boolean;
    age?: string;
  };
};

interface FamilyHistoryFormProps extends Omit<HistoryProps, "item"> {
  item: FamilyHistoryConditionFormatted;
  allItems: FamilyHistory[];
}

const filter = createFilterOptions<RelationshipOption>({
  stringify: (option) => option.display,
});

const FamilyHistoryFormNew: FC<FamilyHistoryFormProps> = ({
  item,
  allItems,
  patientReference,
  disabled,
  removeHistoryItem,
  setSaved,
  updateHistoryItem,
  onRelationshipDeceasedChange,
}) => {
  const [intItem, setIntItem] = useState<FamilyHistoryConditionFormatted>(item);
  const [originalItems, setOriginalItems] = useState<FamilyHistory[]>(allItems);
  const [saveableItems, setSaveableItems] = useState<FamilyHistory[]>(allItems);

  useEffect(() => {
    setOriginalItems(allItems);
    setSaveableItems((prev) => overlayRelationshipDeceased(prev, allItems));
  }, [allItems]);

  useEffect(() => {
    setIntItem((prev) => {
      if (
        !sameConditionCode(
          conditionCodeOf(prev?.condition),
          conditionCodeOf(item?.condition),
        )
      ) {
        return item;
      }

      return {
        ...prev,
        ids: item.ids?.length ? item.ids : prev.ids,
        relationships: [
          ...(prev.relationships || []).map((rel) => {
            const incoming = (item.relationships || []).find(
              (other) => relationshipCode(other) === relationshipCode(rel),
            );
            if (!incoming) {
              return rel;
            }

            return {
              ...rel,
              deceased: incoming.deceased,
              id: incoming.id ?? rel.id,
              ids: incoming.ids ?? rel.ids,
            };
          }),
          ...(item.relationships || []).filter(
            (incoming) =>
              !(prev.relationships || []).some(
                (rel) => relationshipCode(rel) === relationshipCode(incoming),
              ),
          ),
        ],
      };
    });
  }, [item]);

  const { encounterReference, refreshPatientHistory } = useMedicalEncounter();
  const { setNotification } = useNotifications();
  const practitionerReference = usePractitioner();
  const [loading, toggleLoader] = useState(false);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [relationshipStatuses, setRelationshipStatuses] =
    useState<RelationshipStatus>({});

  const handleAgeChange = (val: QuantityType) => {
    if (val.value !== undefined) {
      return {
        ...val,
        value: val.value.toString(),
      };
    }
    return val;
  };

  const toAnnotationNotes = (notes?: NoteWithUuid[]) =>
    notes?.map((note) => ({
      uuid: note.uuid,
      author: {
        authorReference: note.author,
        authorString: note.author.reference || "",
        display: note.author.display || "",
      },
      text: note.text,
      time: note.time,
    }));

  const updateSaveableItems = (
    relationships: FamilyHistoryConditionFormatted["relationships"] = [],
    condition: CodeableConcept = {},
    existingItems: FamilyHistory[] = [],
    notes?: NoteWithUuid[],
  ) =>
    buildSaveableFamilyHistoryItems({
      relationships,
      condition,
      originalItems,
      existingItems,
      annotationNotes: toAnnotationNotes(notes),
    });

  const updateMember = (field, value) => {
    const member = { ...intItem };
    set(member, field, value);
    setIntItem(member);

    // Handle notes with UUID management
    if (field === "note" && value) {
      const currentNotes = get(member, "notes", []) as NoteWithUuid[];
      let updatedNotes: NoteWithUuid[] = [];

      if (Array.isArray(value)) {
        // Process each note in the array
        updatedNotes = value.map((note) => {
          // If note doesn't have a UUID, create one
          if (!note.uuid) {
            return noteManager.createNote(note.text || "", note.author);
          }
          return note;
        });

        // Remove any duplicate UUIDs to prevent multi-render issues
        updatedNotes = noteManager.deduplicateNotesByUuid(updatedNotes);
      }

      set(member, "notes", updatedNotes);
    }

    // Update saveable items when relationships, condition, or notes change
    const updatedSaveableItems = updateSaveableItems(
      member.relationships,
      member.condition,
      saveableItems,
      get(member, "notes"),
    );

    if (updatedSaveableItems) setSaveableItems(updatedSaveableItems);
  };

  const deleteMember = (removedCode: string) => {
    setSaveableItems((currentItems) =>
      applyRemovedFamilyHistoryRelationship(
        currentItems,
        removedCode,
        conditionCodeOf(intItem.condition),
      ),
    );

    const updatedRelationships = intItem.relationships.filter(
      (r) => r.relationship.coding[0].code !== removedCode,
    );
    setIntItem({
      ...intItem,
      relationships: updatedRelationships,
    });
    setSaved(false);
  };

  const deleteAll = async () => {
    try {
      toggleLoader(true);

      const itemsToUpdate = getFamilyHistoryItemsToDeleteCondition({
        originalItems,
        saveableItems,
        conditionCode: conditionCodeOf(intItem.condition),
      });

      const results = await Promise.all(
        itemsToUpdate.map((updatedItem) =>
          medicalsApi.updateFamilyHistory(
            updatedItem.id,
            updatedItem,
            createEncounterParam(encounterReference),
          ),
        ),
      );

      const successfulUpdates = results.filter(Boolean);
      setSaveableItems((prev) =>
        prev.map((item) => {
          const updated = successfulUpdates.find((u) => u.id === item?.id);
          return updated || item;
        }),
      );

      updateHistoryItem?.(successfulUpdates);
      refreshPatientHistory();
      setNotification({
        variant: NotificationVariant.success,
        message: "Successfully updated family history",
      });

      setOpenDialog(false);
      removeHistoryItem();
    } catch (error) {
      console.error("Error updating family history:", error);
      setNotification({
        variant: NotificationVariant.error,
        message: error.message || "Failed to update family history",
      });
    } finally {
      toggleLoader(false);
    }
  };

  const saveFamilyHistory = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      toggleLoader(true);

      const itemsToPersist = getFamilyHistoryItemsToPersist({
        saveableItems:
          updateSaveableItems(
            intItem.relationships,
            intItem.condition,
            saveableItems,
            get(intItem, "notes"),
          ) ?? saveableItems,
        originalItems,
        currentRelationships: intItem.relationships,
        conditionCode: conditionCodeOf(intItem.condition),
        stripUnselectedRelationships: Boolean(intItem.ids?.length),
      });

      if (!itemsToPersist.length) {
        setNotification({
          variant: NotificationVariant.warning,
          message: "Please add family history items before saving",
        });
        return;
      }

      const savedItems = await Promise.all(
        itemsToPersist.map(async (memberHistory) => {
          if (patientReference) {
            memberHistory.patient = patientReference;
          }

          if (encounterReference) {
            memberHistory.encounter = encounterReference;
          }

          const response = memberHistory.id
            ? await medicalsApi.updateFamilyHistory(
                memberHistory.id,
                {
                  ...memberHistory,
                  status: memberHistory.status || "partial",
                },
                createEncounterParam(encounterReference),
              )
            : await medicalsApi.saveFamilyHistory(
                {
                  ...memberHistory,
                  status: memberHistory.status || "partial",
                },
                createEncounterParam(encounterReference),
              );

          return {
            ...response,
            deceased: memberHistory.deceased,
          };
        }),
      );

      setSaveableItems(savedItems);
      updateHistoryItem?.(savedItems);
      refreshPatientHistory();
      setNotification({
        variant: NotificationVariant.success,
        message: `Successfully saved the patient's family history entries.`,
      });
      setSaved(true);
    } catch (e) {
      console.error(e);
      setNotification({
        variant: NotificationVariant.error,
        message: e.message,
      });
    } finally {
      toggleLoader(false);
    }
  };

  const onDelete = () => {
    if (intItem?.ids?.length) {
      setOpenDialog(true);
    } else {
      try {
        removeHistoryItem();
      } catch (error) {
        console.error(error);
      }
    }
  };

  const initializeRelationshipStatuses = (
    relationships: FamilyHistoryConditionFormatted["relationships"] = [],
  ): RelationshipStatus => {
    const newStatuses: RelationshipStatus = {};
    relationships.forEach((rel) => {
      const { code } = rel.relationship.coding[0];
      newStatuses[code] = {
        isDeceased: Boolean(rel.deceased?.value),
        age: rel.deceased?.value,
      };
    });
    return newStatuses;
  };

  useEffect(() => {
    setRelationshipStatuses(
      initializeRelationshipStatuses(get(intItem, "relationships", [])),
    );
  }, [intItem.relationships]);

  return (
    <form onSubmit={saveFamilyHistory}>
      <div>
        <DialogPrompt
          openPrompt={openDialog}
          content={`You are about to delete the entire family history for this patient's ${get(
            intItem,
            "relationship.text",
          )}, and this action is permanent. Are you sure?`}
          title="Delete Family Condition"
          button1="Cancel"
          button2="Delete"
          button1Click={() => setOpenDialog(false)}
          // button2Click={deleteFamilyHistory}
          button2Click={deleteAll}
        />
        <Grid container spacing={1} px={1} my={0.5}>
          <ConditionInputNew
            disabled={disabled}
            intItem={intItem}
            onConditionUpdate={(val) => {
              updateMember("condition", val);
              setSaved(false);
            }}
          />
          <Grid item xs={12}>
            <FormControl fullWidth>
              <AutocompleteFreeOptions
                type="family-member"
                multiple
                autoHighlight
                disabled={disabled}
                id="family-history-relationship"
                options={familyHistoryRelationships as RelationshipOption[]}
                filterOptions={(options, params) => {
                  const { inputValue } = params;

                  if (!inputValue) {
                    return options;
                  }

                  const filtered = filter(options, params);

                  const isExisting = options.some(
                    (option) =>
                      inputValue.toLowerCase() === option.display.toLowerCase(),
                  );

                  if (inputValue !== "" && !isExisting) {
                    filtered.push({
                      code: kebabCase(`other-${inputValue}`),
                      display: `Add "${inputValue}"`,
                      isCreatable: true,
                    } as RelationshipOption);
                  }

                  return filtered;
                }}
                value={get(intItem, "relationships", []).map((rel) => {
                  const coding = rel.relationship.coding[0];
                  const predefined = familyHistoryRelationships.find(
                    (option) => option.code === coding.code,
                  );
                  if (predefined) return predefined;

                  return {
                    code: coding.code,
                    display: coding.display,
                  } as RelationshipOption;
                })}
                isOptionEqualToValue={(option, value) =>
                  option.code === value?.code
                }
                getOptionLabel={(option: RelationshipOption) => {
                  if (typeof option === "string") return option;
                  return option?.display || "";
                }}
                onChange={(
                  _,
                  newValue: RelationshipOption[],
                  reason,
                  details,
                ) => {
                  if (reason === "removeOption" && details?.option) {
                    deleteMember(details.option.code);
                    return;
                  }
                  if (typeof newValue === "string" || newValue === null) {
                    return;
                  }
                  const relationships = newValue.map((relation) => {
                    const existingRelationship = get(
                      intItem,
                      "relationships",
                      [],
                    ).find(
                      (rel) =>
                        rel.relationship.coding[0].code === relation.code,
                    );

                    if (existingRelationship) {
                      return existingRelationship;
                    }
                    return {
                      relationship: {
                        coding: [
                          {
                            system: CodingSystems.FAMILY_MEMBER,
                            code: relation.code,
                            display: relation.display,
                          },
                        ],
                        text: relation.display,
                      },
                      sex: {
                        coding: [
                          {
                            system: CodingSystems.FAMILY_MEMBER,
                            code: "unknown",
                            display: "Unknown",
                          },
                        ],
                        text: "Unknown",
                      },
                      deceased: relationshipStatuses[relation.code]?.isDeceased
                        ? {
                            value:
                              relationshipStatuses[relation.code]?.age || "0",
                          }
                        : undefined,
                    };
                  });

                  updateMember("relationships", relationships);

                  setSaved(false);
                }}
                textFieldProps={{
                  label: "Relationship",
                  required: !get(
                    intItem,
                    "relationships[0].relationship.coding[0].code",
                  ),
                  InputLabelProps: { shrink: true },
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={12} container spacing={2}>
            {Object.entries(relationshipStatuses).length > 0 && (
              <Grid item xs={12} mt={2}>
                <Typography variant="subtitle2">Deceased Status</Typography>
              </Grid>
            )}
            {get(intItem, "relationships", []).map((rel, index) => (
              <Grid
                item
                xs={12}
                container
                spacing={2}
                key={`deceased-status-${rel.relationship.coding[0].code}`}
              >
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    checked={
                      relationshipStatuses[rel.relationship.coding[0].code]
                        ?.isDeceased ?? false
                    }
                    onChange={(e, checked) => {
                      const { code } = rel.relationship.coding[0];
                      setRelationshipStatuses((prev) => ({
                        ...prev,
                        [code]: {
                          ...prev[code],
                          isDeceased: checked,
                          age: checked ? prev[code]?.age || "0" : undefined,
                        },
                      }));

                      const deceased = checked ? { value: "0" } : {};
                      updateMember(
                        `relationships[${index}].deceased`,
                        deceased,
                      );
                      onRelationshipDeceasedChange?.(code, deceased);
                      setSaved(false);
                    }}
                    disabled={disabled}
                    control={
                      <Checkbox
                        name={`deceased-${rel.relationship.coding[0].code}`}
                        color="primary"
                      />
                    }
                    label={`${rel.relationship.coding[0].display} Deceased`}
                  />
                </Grid>
                {relationshipStatuses[rel.relationship.coding[0].code]
                  ?.isDeceased && (
                  <Grid item xs={12} md={8}>
                    <Quantity
                      disabled={disabled}
                      fieldLabel={`${rel.relationship.coding[0].display} Deceased Age`}
                      quantity={{
                        value:
                          relationshipStatuses[rel.relationship.coding[0].code]
                            ?.age || "0",
                      }}
                      setQuantity={(val) => {
                        const ageValue = handleAgeChange(val);
                        setRelationshipStatuses((prev) => ({
                          ...prev,
                          [rel.relationship.coding[0].code]: {
                            ...prev[rel.relationship.coding[0].code],
                            age: ageValue.value,
                          },
                        }));

                        updateMember(
                          `relationships[${index}].deceased`,
                          ageValue,
                        );
                        onRelationshipDeceasedChange?.(
                          rel.relationship.coding[0].code,
                          ageValue,
                        );
                        setSaved(false);
                      }}
                      defaultUnitCode="a"
                      optionKey="timeSpans"
                      codingSystem={CodingSystems.UNITS_OF_MEASURE}
                      size={undefined}
                      min={0}
                    />
                  </Grid>
                )}
              </Grid>
            ))}
          </Grid>
          <Grid item xs={12}>
            <Grid item xs={12} my={2}>
              <Divider />
            </Grid>
            <Grid item xs={12} sx={{ marginTop: 2 }}>
              <NotesInput
                value={get(intItem, "notes", [])}
                disabled={disabled}
                label="Comments/Notes"
                onChange={(notes) => {
                  updateMember("note", notes);
                  setSaved(false);
                }}
                required
                placeholderText="Enter comments/notes about this family member"
              />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" sx={{ gap: 1 }}>
              <Button
                color="secondary"
                variant="outlined"
                size="small"
                disabled={disabled}
                onClick={onDelete}
              >
                Delete
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                size="small"
                disabled={disabled}
              >
                Save
              </Button>
            </Box>
          </Grid>
        </Grid>
      </div>
      <ComponentLoader loading={loading} />
    </form>
  );
};

export default FamilyHistoryFormNew;
